import React, { memo, useCallback, useMemo, useRef, useState } from 'react';

import { Download, TableChart } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import currencyFormatter from '~/utils/currencyFormatter';

import { HEATMAP_COLORS, SUPPLIER_CHIP_COLORS } from './constants';
import { ParityAnalysis, TRACKED_OTAS } from './types';
import { fmtMonthHeader, fmtShortDate } from './utils';

function getHeatmapColors(price: number | null, bestPrice: number | null): { bgcolor: string; color: string } | null {
  if (price == null || bestPrice == null || bestPrice === 0) {
    return null;
  }

  const pctAboveBest = ((price - bestPrice) / bestPrice) * 100;

  if (pctAboveBest <= 2) {
    return HEATMAP_COLORS.good;
  }
  if (pctAboveBest <= 5) {
    return HEATMAP_COLORS.slight;
  }
  if (pctAboveBest <= 10) {
    return HEATMAP_COLORS.moderate;
  }
  return HEATMAP_COLORS.severe;
}

interface Props {
  analysis: ParityAnalysis;
}

function CalendarRateView({ analysis }: Props) {
  const [hiddenSuppliers, setHiddenSuppliers] = useState<Set<string>>(new Set());

  const dates = useMemo(() => analysis.dailyDetail.map((d) => d.date), [analysis.dailyDetail]);

  const supplierPrices = useMemo(() => {
    const map: Record<string, Record<string, number | null>> = {};

    map['Luxury Escapes'] = {};
    for (const d of analysis.dailyDetail) {
      map['Luxury Escapes'][d.date] = d.lePrice;
    }

    for (const ota of TRACKED_OTAS) {
      map[ota] = {};
      for (const d of analysis.dailyDetail) {
        map[ota][d.date] = d.allPrices[ota] ?? null;
      }
    }

    map['Best Competitor'] = {};
    for (const d of analysis.dailyDetail) {
      map['Best Competitor'][d.date] = d.bestCompetitorPrice;
    }

    return map;
  }, [analysis.dailyDetail]);

  const monthGroups = useMemo(() => {
    const groups: Array<{ month: string; count: number }> = [];
    let lastMonth = '';

    for (const d of analysis.dailyDetail) {
      const month = fmtMonthHeader(d.date);
      if (month !== lastMonth) {
        groups.push({ month, count: 1 });
        lastMonth = month;
      } else {
        groups[groups.length - 1].count++;
      }
    }

    return groups;
  }, [analysis.dailyDetail]);

  const visibleRows = useMemo(() => {
    const otas: Array<string> = [...TRACKED_OTAS].filter((ota) => !hiddenSuppliers.has(ota));
    return [...otas, 'Luxury Escapes', 'Best Competitor'];
  }, [hiddenSuppliers]);

  const toggleSupplier = (name: string) => {
    setHiddenSuppliers((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  const fmtPrice = (price: number | null) => (price != null ? currencyFormatter(analysis.currency, price, 0) : '-');

  const lowestPriceByDate = useMemo(() => {
    const map: Record<string, number | null> = {};
    for (const d of analysis.dailyDetail) {
      const candidates = [d.lePrice, d.bestCompetitorPrice].filter((p): p is number => p != null);
      map[d.date] = candidates.length > 0 ? Math.min(...candidates) : null;
    }
    return map;
  }, [analysis.dailyDetail]);

  const getCellHeatmap = (supplier: string, date: string) => {
    const price = supplierPrices[supplier]?.[date] ?? null;
    const lowestPrice = lowestPriceByDate[date] ?? null;
    return getHeatmapColors(price, lowestPrice);
  };

  const handleDownloadCsv = useCallback(() => {
    const allSuppliers = [...TRACKED_OTAS, 'Luxury Escapes', 'Best Competitor'];
    const header = ['Supplier', ...dates.map((d) => fmtShortDate(d))].join(',');
    const rows = allSuppliers.map((supplier) => {
      const prices = dates.map((date) => {
        const price = supplierPrices[supplier]?.[date];
        return price != null ? price : '';
      });
      return [`"${supplier}"`, ...prices].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parity_matrix_${analysis.currency}_${dates[0] ?? 'unknown'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [dates, supplierPrices, analysis.currency]);

  const tableRef = useRef<HTMLDivElement>(null);

  const handleShareScreenshot = useCallback(async () => {
    if (!tableRef.current) {
      return;
    }
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(tableRef.current, { scale: 2, useCORS: true });
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `parity_matrix_${analysis.currency}_${dates[0] ?? 'unknown'}.png`;
    a.click();
  }, [analysis.currency, dates]);

  const stickyColumnSx = {
    position: 'sticky' as const,
    left: 0,
    zIndex: 1,
    bgcolor: 'background.paper',
    whiteSpace: 'nowrap',
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 600 }}>Competitive Calendar Matrix</Typography>
        <Box>
          <IconButton onClick={handleShareScreenshot} title="Download snapshot">
            <Download />
          </IconButton>
          <IconButton onClick={handleDownloadCsv} title="Download CSV">
            <TableChart />
          </IconButton>
        </Box>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>Filter Suppliers:</Typography>
        {TRACKED_OTAS.map((ota) => {
          const active = !hiddenSuppliers.has(ota);
          const colors = SUPPLIER_CHIP_COLORS[ota];
          return (
            <Box
              key={ota}
              component="span"
              onClick={() => toggleSupplier(ota)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                px: 2,
                py: 1,
                borderRadius: '8px',
                fontSize: 13,
                fontWeight: 600,
                border: '2px solid',
                borderColor: colors.border,
                bgcolor: colors.bg,
                color: colors.color,
                cursor: 'pointer',
                opacity: active ? 1 : 0.4,
              }}
            >
              {active && '✓ '}
              {ota}
            </Box>
          );
        })}
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 2,
            py: 1,
            borderRadius: '8px',
            fontSize: 13,
            fontWeight: 600,
            border: 2,
            borderColor: 'grey.400',
            bgcolor: 'grey.100',
            color: 'text.primary',
            opacity: 0.6,
            cursor: 'default',
            userSelect: 'none',
          }}
        >
          ✓ Luxury Escapes
        </Box>
      </Stack>

      <TableContainer ref={tableRef} component={Paper} sx={{ maxHeight: 600, overflow: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                }}
              />
              {monthGroups.map((group) => (
                <TableCell
                  key={group.month}
                  colSpan={group.count}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                  }}
                >
                  {group.month}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell
                sx={{
                  ...stickyColumnSx,
                  zIndex: 3,
                  bgcolor: 'grey.100',
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                }}
              />
              {dates.map((date) => (
                <TableCell
                  key={date}
                  align="center"
                  sx={{
                    bgcolor: 'grey.100',
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {fmtShortDate(date)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.map((supplier) => (
              <TableRow key={supplier}>
                <TableCell
                  sx={{
                    ...stickyColumnSx,
                    fontWeight: 600,
                  }}
                >
                  {supplier}
                </TableCell>
                {dates.map((date) => {
                  const price = supplierPrices[supplier]?.[date] ?? null;
                  const heatmap = getCellHeatmap(supplier, date);
                  return (
                    <TableCell
                      key={date}
                      align="right"
                      sx={{
                        py: 2,
                        px: 1,
                        fontSize: 14,
                        fontWeight: 600,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      {heatmap ? (
                        <Box
                          component="span"
                          sx={{
                            ...heatmap,
                            borderRadius: '6px',
                            px: 1,
                            py: 0.5,
                          }}
                        >
                          {fmtPrice(price)}
                        </Box>
                      ) : (
                        fmtPrice(price)
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default memo(CalendarRateView);
